import { useCallback, useEffect, useState } from 'react';
import { listBatchHistory } from '../api';
import type { BatchHistoryItem } from '../types';

/**
 * Finished CSV batches — every job in them has reached a terminal status.
 * Parity with lightning's and interior's batch history, restyled in the
 * video palette, with the aggregation (success rate, duration, cost) done
 * server-side in `video.db.get_completed_video_batches`.
 */

const PAGE_SIZE = 25;

function formatDuration(seconds: number | null): string {
  if (seconds == null) return '—';
  const total = Math.max(0, Math.round(seconds));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m`;
  if (m > 0) return `${m}m ${String(s).padStart(2, '0')}s`;
  return `${s}s`;
}

function formatTime(value: string | null): string {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return '—';
  }
}

function formatCost(value: number | null): string {
  if (value == null) return '—';
  return `$${value.toFixed(2)}`;
}

export function BatchHistoryTable() {
  const [batches, setBatches] = useState<BatchHistoryItem[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (targetPage: number) => {
    setLoading(true);
    setError(null);
    try {
      const data = await listBatchHistory({ page: targetPage, pageSize: PAGE_SIZE });
      setBatches(data.batches);
      setTotalPages(data.total_pages);
      setTotal(data.total);
    } catch (ex) {
      setError(ex instanceof Error ? ex.message : 'Failed to load batch history');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(page);
  }, [load, page]);

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
        Failed to load: {error}
      </div>
    );
  }

  if (loading && batches.length === 0) {
    return <div className="text-sm text-primary-400">Loading…</div>;
  }

  if (batches.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-primary-200 bg-white px-6 py-10 text-center text-primary-500 text-sm">
        No finished batches yet. Submit a CSV from{' '}
        <span className="font-semibold">Generate Scan</span> to create one.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="overflow-hidden rounded-2xl border border-primary-100 bg-white shadow-card">
        <table className="data-table mobile-collapsible min-w-full text-sm">
          <thead className="bg-primary-50/60 text-primary-600 text-xs uppercase tracking-wider">
            <tr>
              <th className="px-4 py-3 text-left font-semibold">CSV File</th>
              <th className="px-4 py-3 text-left font-semibold">Scans</th>
              <th className="px-4 py-3 text-left font-semibold">Completed</th>
              <th className="px-4 py-3 text-left font-semibold">Failed</th>
              <th className="px-4 py-3 text-left font-semibold">Success</th>
              <th className="px-4 py-3 text-left font-semibold">Duration</th>
              <th className="px-4 py-3 text-left font-semibold">Cost</th>
              <th className="px-4 py-3 text-left font-semibold">Finished</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-primary-100">
            {batches.map((batch) => (
              <tr key={batch.batch_id} className="hover:bg-primary-50/40">
                <td
                  className="px-4 py-3 text-primary-900 font-medium break-all"
                  data-label="CSV File"
                >
                  {batch.filename}
                </td>
                <td className="px-4 py-3 font-mono text-primary-700" data-label="Scans">
                  {batch.total_scans}
                </td>
                <td className="px-4 py-3 font-mono text-green-700" data-label="Completed">
                  {batch.completed}
                </td>
                <td
                  className={`px-4 py-3 font-mono ${batch.failed > 0 ? 'text-red-700' : 'text-primary-400'}`}
                  data-label="Failed"
                >
                  {batch.failed}
                </td>
                <td className="px-4 py-3" data-label="Success">
                  <span
                    className={`font-mono font-medium ${
                      batch.success_percentage >= 90
                        ? 'text-green-600'
                        : batch.success_percentage >= 70
                          ? 'text-amber-600'
                          : 'text-red-600'
                    }`}
                  >
                    {batch.success_percentage.toFixed(0)}%
                  </span>
                </td>
                <td className="px-4 py-3 font-mono text-primary-700" data-label="Duration">
                  {formatDuration(batch.duration_seconds)}
                </td>
                <td className="px-4 py-3 font-mono text-primary-700" data-label="Cost">
                  {formatCost(batch.total_cost)}
                </td>
                <td className="px-4 py-3 text-primary-700" data-label="Finished">
                  {formatTime(batch.completed_at)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-primary-500">
            Page <span className="font-semibold text-primary-900">{page}</span> of {totalPages} ·{' '}
            {total} batch{total === 1 ? '' : 'es'}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1 || loading}
              className="px-3 py-1.5 rounded-lg border border-primary-200 text-primary-700 hover:bg-primary-50 transition-colors disabled:opacity-50"
            >
              Previous
            </button>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages || loading}
              className="px-3 py-1.5 rounded-lg border border-primary-200 text-primary-700 hover:bg-primary-50 transition-colors disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
